import { GrElement, TOKENS_CSS } from "../core/base";

/**
 * <gr-monitor device="co2"></gr-monitor>          every bus message for one device
 * <gr-monitor></gr-monitor>                        every message on the bus
 *
 * The escape hatch: always shows what's really flowing, so a broken
 * widget binding or firmware mismatch is debuggable instead of a dead
 * page. Line transports may opt into a raw send box with `raw-send`.
 */

const MAX_ROWS = 400;
const FLUSH_INTERVAL_MS = 50;
const timeFormatter = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
type PendingRow = { tag: string; text: string; time: string };

export class GrMonitor extends GrElement {
  private list!: HTMLElement;
  private autoscroll = true;
  private expectedScrollTop: number | null = null;
  private pendingRows: PendingRow[] = [];
  private flushTimer: number | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private touchY: number | null = null;
  private historyAnchor: { row: HTMLElement; offset: number } | null = null;

  connectedCallback(): void {
    const device = this.attr("device");
    const rawSend = device && this.hasAttribute("raw-send");
    const root = this.shadowRoot ?? this.attachShadow({ mode: "open" });
    this.autoscroll = true;
    this.expectedScrollTop = null;
    this.historyAnchor = null;
    root.innerHTML = `
      <style>
        ${TOKENS_CSS}
        :host {
          display: flex;
          flex-direction: column;
          border-radius: var(--_radius);
          background: rgba(0, 0, 0, 0.35);
          border: 1px solid var(--_border);
          overflow: hidden;
          min-height: 120px;
        }
        header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 14px;
          border-bottom: 1px solid var(--_border);
        }
        .list {
          flex: 1;
          overflow-y: auto;
          padding: 10px 14px;
          font-family: var(--_mono);
          font-size: 11px;
          line-height: 1.7;
          max-height: var(--gr-monitor-height, 220px);
          overflow-anchor: none;
          scroll-behavior: auto;
        }
        .row { display: flex; gap: 10px; white-space: pre-wrap; word-break: break-all; }
        .ts { color: var(--_muted); flex-shrink: 0; }
        .topic { color: var(--_accent); flex-shrink: 0; }
        form { display: flex; border-top: 1px solid var(--_border); }
        input {
          flex: 1;
          background: transparent;
          border: none;
          outline: none;
          color: var(--_text);
          font-family: var(--_mono);
          font-size: 12px;
          padding: 10px 14px;
        }
        button {
          background: transparent;
          border: none;
          border-left: 1px solid var(--_border);
          color: var(--_accent);
          font-family: var(--_font);
          font-size: 10px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          padding: 0 16px;
          cursor: pointer;
        }
      </style>
      <header>
        <span class="label">${device ? device + " · bus" : "bus monitor"}</span>
      </header>
      <div class="list" tabindex="0"></div>
      ${rawSend ? '<form><input placeholder="send a raw line…" /><button type="submit">Send</button></form>' : ""}
    `;
    this.list = root.querySelector(".list")!;
    this.list.addEventListener("scroll", () => {
      const el = this.list;
      // Scroll events are queued. More messages can arrive between setting
      // scrollTop and this event; growth below that position is not user intent.
      if (this.expectedScrollTop !== null && Math.abs(el.scrollTop - this.expectedScrollTop) <= 1) return;
      this.expectedScrollTop = null;
      this.autoscroll = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
      if (this.autoscroll) this.historyAnchor = null;
      else this.rememberAnchor();
    });
    this.list.addEventListener("wheel", (event) => {
      if (event.deltaY < 0 && this.list.scrollHeight > this.list.clientHeight) this.autoscroll = false;
    }, { passive: true });
    this.list.addEventListener("keydown", (event) => {
      if (["ArrowUp", "PageUp", "Home"].includes(event.key) || (event.key === " " && event.shiftKey)) {
        if (this.list.scrollHeight > this.list.clientHeight) this.autoscroll = false;
      }
    });
    this.list.addEventListener("touchstart", (event) => { this.touchY = event.touches[0]?.clientY ?? null; }, { passive: true });
    this.list.addEventListener("touchmove", (event) => {
      const next = event.touches[0]?.clientY;
      if (next !== undefined && this.touchY !== null && next > this.touchY && this.list.scrollHeight > this.list.clientHeight) this.autoscroll = false;
      this.touchY = next ?? null;
    }, { passive: true });
    this.list.addEventListener("touchend", () => { this.touchY = null; }, { passive: true });
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.autoscroll) {
          this.list.scrollTop = this.list.scrollHeight;
          this.expectedScrollTop = this.list.scrollTop;
        }
      });
      this.resizeObserver.observe(this.list);
    }

    const form = root.querySelector("form");
    form?.addEventListener("submit", (e) => {
      e.preventDefault();
      const input = form.querySelector("input")!;
      if (input.value.trim()) {
        this.bus.publish(`${device}/$send`, input.value, { src: "gr-monitor", retain: false });
        this.appendRow("→", input.value);
        input.value = "";
      }
    });

    const pattern = device ? `${device}/#` : "#";
    this.track(
      this.bus.subscribe(
        pattern,
        (msg) => {
          if (device) {
            const signal = msg.topic.slice(device.length + 1);
            this.appendRow(
              signal === "$raw" ? "← raw" : signal,
              typeof msg.payload === "string" ? msg.payload : JSON.stringify(msg.payload),
            );
          } else {
            if (msg.topic.endsWith("/$raw")) return;
            this.appendRow(msg.topic, JSON.stringify(msg.payload));
          }
        },
        { replay: false },
      ),
    );
  }

  private appendRow(tag: string, text: string): void {
    this.pendingRows.push({ tag, text, time: timeFormatter.format(Date.now()) });
    if (this.pendingRows.length > MAX_ROWS) this.pendingRows.splice(0, this.pendingRows.length - MAX_ROWS);
    if (this.flushTimer === null) this.flushTimer = window.setTimeout(() => this.flushRows(), FLUSH_INTERVAL_MS);
  }

  private flushRows(): void {
    this.flushTimer = null;
    const batch = this.pendingRows.splice(0);
    if (!this.isConnected || !batch.length) return;
    const list = this.list;
    // Catch a scrollbar drag even if its scroll event has not run yet.
    if (this.expectedScrollTop !== null && Math.abs(list.scrollTop - this.expectedScrollTop) > 1) {
      this.autoscroll = list.scrollTop + list.clientHeight >= list.scrollHeight - 8;
      if (!this.autoscroll) this.rememberAnchor();
    }
    if (!this.autoscroll && !this.historyAnchor) this.rememberAnchor();
    const previousTop = list.scrollTop;
    const removeCount = Math.max(0, list.childElementCount + batch.length - MAX_ROWS);
    let removedHeight = 0;
    let removed = list.firstElementChild;
    // Measure before mutating to avoid per-message layout work.
    for (let index = 0; index < removeCount && removed; index++) {
      removedHeight += removed.getBoundingClientRect().height;
      removed = removed.nextElementSibling;
    }
    const fragment = document.createDocumentFragment();
    for (const entry of batch) fragment.appendChild(this.createRow(entry));
    for (let index = 0; index < removeCount; index++) list.firstElementChild?.remove();
    list.appendChild(fragment);
    if (this.autoscroll) {
      list.scrollTop = list.scrollHeight;
      this.historyAnchor = null;
    } else if (this.historyAnchor?.row.isConnected) {
      // Keep the original user-selected offset across every batch. Repeatedly
      // subtracting fractional row heights would accumulate scrollTop rounding.
      list.scrollTop += this.historyAnchor.row.getBoundingClientRect().top - list.getBoundingClientRect().top - this.historyAnchor.offset;
    } else {
      if (removeCount) list.scrollTop = Math.max(0, previousTop - removedHeight);
      this.rememberAnchor();
    }
    this.expectedScrollTop = list.scrollTop;
  }

  private rememberAnchor(): void {
    const top = this.list.getBoundingClientRect().top;
    const row = Array.from(this.list.children).find(row => row.getBoundingClientRect().bottom > top) as HTMLElement | undefined;
    this.historyAnchor = row ? { row, offset: row.getBoundingClientRect().top - top } : null;
  }

  private createRow(entry: PendingRow): HTMLElement {
    const row = document.createElement("div");
    row.className = "row";
    const ts = document.createElement("span");
    ts.className = "ts";
    ts.textContent = entry.time;
    const topic = document.createElement("span");
    topic.className = "topic";
    topic.textContent = entry.tag;
    const body = document.createElement("span");
    body.textContent = entry.text;
    row.append(ts, topic, body);
    return row;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.flushTimer !== null) window.clearTimeout(this.flushTimer);
    this.flushTimer = null;
    this.pendingRows = [];
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.historyAnchor = null;
  }
}

customElements.define("gr-monitor", GrMonitor);
